import { useState, type FormEvent } from 'react';
import { Building2, Copy, History, LogOut, Pause, Play, Plus, ShieldCheck, UserPlus } from 'lucide-react';
import { AsyncView, Button, Chip, Input, Notice, Select, Stat, Table, useToast, type Column } from '../ui';
import { Drawer } from '../ui/Drawer';
import { Kebab } from '../ui/Kebab';
import { api, ApiError } from '../lib/api';
import { useResource } from '../lib/useResource';
import { useRevalidarSessao, type Master } from '../lib/session';
import './console-master.css';

interface Empresa {
  id: string;
  name: string;
  slug: string;
  status: 'ACTIVE' | 'SUSPENDED';
  createdAt: string;
  _count: { agents: number; wabaAccounts: number; contacts: number };
}

/** O que o servidor devolve UMA vez e nunca mais: a senha temporária só existe
 *  nesta resposta, não é recuperável depois. Por isso ela não vira um toast
 *  que some em cinco segundos — fica na tela até ser descartada. */
interface Convite { email: string; temporaryPassword?: string; contexto: string }
interface EventoAuditoria { id:string; actorEmail:string; action:string; targetType:string; targetId?:string; metadata:unknown; ip?:string; createdAt:string }
const ROTULOS_AUDITORIA: Record<string,string> = { MASTER_LOGIN:'Acesso ao console', TENANT_CREATED:'Empresa criada', MEMBERSHIP_CREATED:'Usuário convidado', TENANT_STATUS_CHANGED:'Situação alterada' };

/**
 * Console da plataforma.
 *
 * Não usa o `shell` do produto de propósito. A navegação lateral lista seções
 * de UM workspace (Caixa de entrada, Campanhas, Automação); um master não está
 * dentro de workspace nenhum, e mostrar aquele menu prometeria acesso a dados
 * que a sessão dele não alcança — as rotas de workspace exigem `tenantId` no
 * cookie, que a sessão master não tem. Seria um menu inteiro de 401.
 */
export function ConsoleMaster({ master }: { master: Master }) {
  const revalidar = useRevalidarSessao();
  const toast = useToast();
  const { state, recarregar } = useResource<Empresa[]>('/platform/tenants');
  const auditoria = useResource<EventoAuditoria[]>('/platform/audit?take=50');
  const [criando, setCriando] = useState(false);
  const [convidandoEm, setConvidandoEm] = useState<Empresa | null>(null);
  const [convite, setConvite] = useState<Convite | null>(null);

  async function sair() {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    revalidar();
  }

  async function alternarStatus(empresa: Empresa) {
    const alvo = empresa.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      await api(`/platform/tenants/${empresa.id}/status`, { method: 'PATCH', body: { status: alvo } });
      toast(alvo === 'SUSPENDED' ? `${empresa.name} foi suspensa.` : `${empresa.name} voltou a ficar ativa.`);
      recarregar();
      auditoria.recarregar();
    } catch (falha) {
      toast(falha instanceof ApiError ? falha.message : 'Falha de conexão.', 'danger');
    }
  }

  const colunas: ReadonlyArray<Column<Empresa>> = [
    {
      key: 'empresa',
      header: 'Empresa',
      cell: (linha) => (
        <div className="console__empresa">
          <strong>{linha.name}</strong>
          <span className="console__slug">{linha.slug}</span>
        </div>
      ),
    },
    { key: 'agents', header: 'Operadores', cell: (linha) => String(linha._count.agents) },
    { key: 'waba', header: 'Números', cell: (linha) => String(linha._count.wabaAccounts) },
    { key: 'contacts', header: 'Contatos', cell: (linha) => String(linha._count.contacts) },
    {
      key: 'status',
      header: 'Situação',
      cell: (linha) => (
        <Chip tone={linha.status === 'ACTIVE' ? 'success' : 'danger'}>
          {linha.status === 'ACTIVE' ? 'Ativa' : 'Suspensa'}
        </Chip>
      ),
    },
    {
      key: 'acoes',
      header: 'Ações',
      align: 'end',
      cell: (linha) => (
        <Kebab
          rotulo={linha.name}
          acoes={[
            {
              rotulo: 'Convidar usuário',
              icone: <UserPlus size={16} aria-hidden="true" />,
              onSelect: () => setConvidandoEm(linha),
              desabilitado: linha.status !== 'ACTIVE',
            },
            linha.status === 'ACTIVE'
              ? {
                  rotulo: 'Suspender empresa',
                  icone: <Pause size={16} aria-hidden="true" />,
                  onSelect: () => void alternarStatus(linha),
                  perigo: true,
                  separar: true,
                }
              : {
                  rotulo: 'Reativar empresa',
                  icone: <Play size={16} aria-hidden="true" />,
                  onSelect: () => void alternarStatus(linha),
                  separar: true,
                },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="console">
      <a className="skip" href="#conteudo">Ir para o conteúdo</a>
      <header className="console__topo">
        <div className="console__marca">
          <ShieldCheck size={20} aria-hidden="true" />
          <span>Console da plataforma</span>
        </div>
        <div className="console__direita">
          <span className="console__quem">
            {master.name}
            <span className="console__email">{master.email}</span>
          </span>
          <Button variant="secondary" size="sm" icon={<LogOut size={16} aria-hidden="true" />} onClick={() => void sair()}>
            Sair
          </Button>
        </div>
      </header>

      <main id="conteudo" tabIndex={-1} data-focus-silent className="console__corpo">
        <div className="console__cabecalho">
          <div>
            <h1 className="console__titulo">Empresas</h1>
            <p className="console__sub">Cada empresa é um workspace isolado, com números, operadores e dados próprios.</p>
          </div>
          <Button variant="primary" icon={<Plus size={18} aria-hidden="true" />} onClick={() => setCriando(true)}>
            Criar empresa
          </Button>
        </div>

        {convite && <PainelConvite convite={convite} onFechar={() => setConvite(null)} />}

        <AsyncView
          state={state}
          label="empresas"
          skeletonRows={4}
          empty={{
            title: 'Nenhuma empresa cadastrada',
            text: 'Crie a primeira empresa para gerar o workspace e o acesso do administrador dela.',
            action: (
              <Button variant="primary" icon={<Plus size={18} aria-hidden="true" />} onClick={() => setCriando(true)}>
                Criar empresa
              </Button>
            ),
          }}
        >
          {(empresas) => (
            <>
              <div className="console__resumo">
                <Stat rotulo="Empresas" valor={empresas.length} />
                <Stat rotulo="Ativas" valor={empresas.filter((e) => e.status === 'ACTIVE').length} />
                <Stat
                  rotulo="Suspensas"
                  valor={empresas.filter((e) => e.status === 'SUSPENDED').length}
                  apoio="Suspensa bloqueia novos convites"
                />
              </div>
              <Table
                caption="Empresas da plataforma, com contagem de operadores, números e contatos"
                columns={colunas}
                rows={empresas}
                rowKey={(linha) => linha.id}
              />
            </>
          )}
        </AsyncView>

        <section className="console__auditoria" aria-labelledby="titulo-auditoria">
          <div className="console__secao-titulo"><History size={20} aria-hidden="true" /><div><h2 id="titulo-auditoria">Atividade administrativa</h2><p>Últimas ações realizadas por usuários Master.</p></div></div>
          <AsyncView state={auditoria.state} label="atividade administrativa" skeletonRows={3} empty={{title:'Nenhuma atividade registrada',text:'Os próximos acessos e alterações administrativas aparecerão aqui.'}}>
            {(eventos) => <Table caption="Últimas ações administrativas" columns={[
              {key:'acao',header:'Ação',cell:(evento:EventoAuditoria)=><strong>{ROTULOS_AUDITORIA[evento.action] ?? evento.action}</strong>},
              {key:'responsavel',header:'Responsável',cell:(evento:EventoAuditoria)=>evento.actorEmail},
              {key:'alvo',header:'Alvo',cell:(evento:EventoAuditoria)=>evento.targetType === 'PLATFORM' ? 'Plataforma' : `${evento.targetType} · ${evento.targetId?.slice(0,8) ?? '—'}`},
              {key:'data',header:'Data',cell:(evento:EventoAuditoria)=>new Intl.DateTimeFormat('pt-BR',{dateStyle:'short',timeStyle:'short'}).format(new Date(evento.createdAt))},
            ]} rows={eventos} rowKey={(evento)=>evento.id} />}
          </AsyncView>
        </section>
      </main>

      <FormularioEmpresa
        aberto={criando}
        onFechar={() => setCriando(false)}
        onCriado={(resultado) => { setCriando(false); setConvite(resultado); recarregar(); auditoria.recarregar(); }}
      />
      <FormularioConvite
        empresa={convidandoEm}
        onFechar={() => setConvidandoEm(null)}
        onConvidado={(resultado) => { setConvidandoEm(null); setConvite(resultado); recarregar(); auditoria.recarregar(); }}
      />
    </div>
  );
}

/** Senha temporária: aparece uma vez, some quando a pessoa disser que copiou. */
function PainelConvite({ convite, onFechar }: { convite: Convite; onFechar: () => void }) {
  const toast = useToast();
  return (
    <Notice tone="success" title={convite.contexto} live>
      <p>
        {convite.temporaryPassword
          ? 'Esta senha aparece uma única vez e não pode ser recuperada. Envie por um canal seguro — não por e-mail junto com o endereço de acesso.'
          : 'Este e-mail já tinha conta na plataforma: a senha atual continua valendo e nenhuma nova foi gerada.'}
      </p>
      <p className="console__credencial">
        <code>{convite.email}</code>
        {convite.temporaryPassword && <code>{convite.temporaryPassword}</code>}
      </p>
      <div className="console__credencial-acoes">
        {convite.temporaryPassword && (
          <Button
            variant="secondary"
            size="sm"
            icon={<Copy size={16} aria-hidden="true" />}
            onClick={() => {
              void navigator.clipboard
                .writeText(`${convite.email} · ${convite.temporaryPassword}`)
                .then(() => toast('Credenciais copiadas.'))
                .catch(() => toast('O navegador bloqueou a cópia. Selecione o texto manualmente.', 'danger'));
            }}
          >
            Copiar
          </Button>
        )}
        <Button variant="tertiary" size="sm" onClick={onFechar}>Já anotei, dispensar</Button>
      </div>
    </Notice>
  );
}

/** `nome-da-empresa` — o servidor exige /^[a-z0-9-]{3,80}$/. Derivar aqui evita
 *  o 400 mais provável do formulário; o campo continua editável. */
function paraSlug(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function FormularioEmpresa({
  aberto, onFechar, onCriado,
}: { aberto: boolean; onFechar: () => void; onCriado: (convite: Convite) => void }) {
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const dados = new FormData(event.currentTarget);
    setErro(null);
    setSalvando(true);
    try {
      const resposta = await api<{ invitation: { email: string; temporaryPassword: string } }>('/platform/tenants', {
        method: 'POST',
        body: {
          companyName: String(dados.get('companyName') ?? ''),
          slug: String(dados.get('slug') ?? ''),
          administratorName: String(dados.get('administratorName') ?? ''),
          administratorEmail: String(dados.get('administratorEmail') ?? ''),
        },
      });
      setSlug('');
      setSlugManual(false);
      onCriado({ ...resposta.invitation, contexto: 'Empresa criada. Envie o acesso ao administrador.' });
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Drawer
      open={aberto}
      onClose={onFechar}
      title="Criar empresa"
      description="Cria o workspace e o primeiro administrador dele, com uma senha temporária."
      footer={
        <>
          <Button variant="tertiary" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="primary"
            type="submit"
            form="form-empresa"
            loading={salvando}
            loadingLabel="Criando a empresa"
            icon={<Building2 size={18} aria-hidden="true" />}
          >
            Criar empresa
          </Button>
        </>
      }
    >
      <form id="form-empresa" onSubmit={enviar} className="console__form">
        {erro && <Notice tone="danger" title="Não foi possível criar" live>{erro}</Notice>}
        <Input
          label="Nome da empresa"
          name="companyName"
          required
          minLength={2}
          maxLength={120}
          onChange={(e) => { if (!slugManual) setSlug(paraSlug(e.currentTarget.value)); }}
        />
        <Input
          label="Identificador"
          name="slug"
          required
          value={slug}
          onChange={(e) => { setSlugManual(true); setSlug(e.currentTarget.value); }}
          pattern="[a-z0-9-]{3,80}"
          hint="Só letras minúsculas, números e hífen. É permanente e aparece em endereços — mudar depois quebra links."
        />
        <Input label="Nome do administrador" name="administratorName" required minLength={2} maxLength={120} />
        <Input
          label="E-mail do administrador"
          name="administratorEmail"
          type="email"
          required
          hint="Recebe papel ADMIN nesta empresa. A senha temporária aparece na tela após criar."
        />
      </form>
    </Drawer>
  );
}

function FormularioConvite({
  empresa, onFechar, onConvidado,
}: { empresa: Empresa | null; onFechar: () => void; onConvidado: (convite: Convite) => void }) {
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function enviar(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!empresa) return;
    const dados = new FormData(event.currentTarget);
    setErro(null);
    setSalvando(true);
    try {
      const resposta = await api<{ email: string; existingUser: boolean; temporaryPassword?: string }>(
        `/platform/tenants/${empresa.id}/memberships`,
        {
          method: 'POST',
          body: {
            name: String(dados.get('name') ?? ''),
            email: String(dados.get('email') ?? ''),
            role: String(dados.get('role') ?? 'AGENT'),
          },
        },
      );
      onConvidado({
        email: resposta.email,
        temporaryPassword: resposta.temporaryPassword,
        contexto: `Usuário adicionado a ${empresa.name}.`,
      });
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Falha de conexão.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Drawer
      open={Boolean(empresa)}
      onClose={onFechar}
      title="Convidar usuário"
      description={empresa ? `Cria o acesso dentro de ${empresa.name}.` : undefined}
      footer={
        <>
          <Button variant="tertiary" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="primary"
            type="submit"
            form="form-convite"
            loading={salvando}
            loadingLabel="Criando o acesso"
            icon={<UserPlus size={18} aria-hidden="true" />}
          >
            Convidar
          </Button>
        </>
      }
    >
      {/* `key` remonta o formulário a cada empresa: sem isso os campos guardam
          o que foi digitado para a empresa anterior. */}
      <form id="form-convite" key={empresa?.id ?? 'nenhuma'} onSubmit={enviar} className="console__form">
        {erro && <Notice tone="danger" title="Não foi possível convidar" live>{erro}</Notice>}
        <Input label="Nome" name="name" required minLength={2} maxLength={120} />
        <Input label="E-mail" name="email" type="email" required />
        <Select
          label="Nível de acesso"
          name="role"
          defaultValue="AGENT"
          options={[
            { value: 'AGENT', label: 'Atendente — só as próprias conversas' },
            { value: 'SUPERVISOR', label: 'Supervisor — métricas e fila de todos' },
            { value: 'ADMIN', label: 'Administrador — configura números, agentes e automação' },
          ]}
          hint="Pode ser alterado depois pelo administrador da empresa."
        />
      </form>
    </Drawer>
  );
}
